import { zodResolver } from '@hookform/resolvers/zod';
import { lazy, type ReactElement, type Ref, Suspense, useImperativeHandle, useState } from 'react';
import type {
  Control,
  DefaultValues,
  FieldPath,
  FieldValues,
  Resolver,
  SubmitErrorHandler,
  SubmitHandler,
  UseFormRegister,
} from 'react-hook-form';
import { FormProvider, useForm } from 'react-hook-form';

import type { SDSFlexboxGap } from '@repo/sds/SDSFlexbox';
import { SDSFlexbox } from '@repo/sds/SDSFlexbox';
import type { FormFieldConfig } from '@repo/ui/fixtures/form/components/FormField/FormField';

import type { FormGroupConfig } from '../FormGroup/FormGroup';
import { FormActionButtons } from './form-action-buttons';
import { FormConfigItemRenderer, getFormConfigItemKey } from './form-config-item-renderer';
import { UNSAVED_CHANGES_MESSAGE } from './form-constants';
import { FormErrorSummary } from './form-error-summary';
import { useFormErrorSummary } from './use-form-error-summary';
import { useFormServerErrorsSync } from './use-form-server-errors-sync';
import { useFormUnsavedChangesGuard } from './use-form-unsaved-changes-guard';

// Union type representing either a FormGroup or a FormField configuration.
export type FormConfigItem<TFieldValues extends FieldValues> =
  | FormGroupConfig<TFieldValues>
  | FormFieldConfig<TFieldValues>;

// The main configuration structure for the entire form.
export type FormConfig<TFieldValues extends FieldValues> = FormConfigItem<TFieldValues>[];

// Defines the imperative API exposed via the component's ref.
export type FormRef<TFieldValues extends FieldValues = FieldValues> = {
  /** Triggers form validation and submission programmatically. */
  submitForm: () => void;
  /** React Hook Form register function for manual field registration. */
  register: ReturnType<typeof useForm>['register'];
  /** React Hook Form control object. */
  control: ReturnType<typeof useForm>['control'];
  /** Attaches a server- or upload-side validation error to a rendered field. */
  setFieldError: (name: FieldPath<TFieldValues>, message: string) => void;
  /** Reports whether the user has changed any value since the latest reset. */
  hasUnsavedChanges: () => boolean;
  /** Accepts the current values as the new saved baseline. */
  markClean: () => void;
};

export interface FormServerErrors {
  readonly [field: string]: readonly string[];
}

export { UNSAVED_CHANGES_MESSAGE };

// The router prompt needs react-router; loading it on demand keeps it out of apps without a data router (landing).
const RouterUnsavedChangesPrompt = lazy(() =>
  import('./router-unsaved-changes-prompt').then(module => ({ default: module.RouterUnsavedChangesPrompt })),
);

export function confirmDiscardUnsavedChanges(isDirty: boolean): boolean {
  return !isDirty || window.confirm(UNSAVED_CHANGES_MESSAGE);
}

interface FormProps<
  TFieldValues extends FieldValues,
  // Generic representing the optional context object passed to the validation resolver.
  TContext = Record<string, unknown>,
  // Generic representing the final data shape after Zod parsing/validation,
  // which might differ from TFieldValues if transforms are used. Constrained to FieldValues.
  TOutputType extends FieldValues = TFieldValues,
> {
  /** Configuration array defining the structure (groups and fields) of the form. */
  formConfig: FormConfig<TFieldValues>;
  /** Specifies the visual gap between form elements arranged in flex containers. */
  fieldsGap?: SDSFlexboxGap;
  /** Text label for the primary submit button. Button is omitted if undefined. */
  submitButtonLabel?: string;
  /** Optional second submit intent, such as publishing while preserving the same entered values. */
  secondarySubmitButtonLabel?: string;
  /** Data attribute for testing the submit button. */
  submitBtnTestId?: string;
  /** The Zod schema used for validating the form data. Maps input (TFieldValues) to output (TOutputType). */
  validationSchema: Parameters<typeof zodResolver>[0] | unknown;
  /** Initial values for the form fields. */
  defaultValues?: DefaultValues<TFieldValues>;
  /** Asynchronous callback function executed with validated data upon successful submission. */
  onSubmit: SubmitHandler<TOutputType>;
  /** Optional callback for the secondary submit intent. */
  onSecondarySubmit?: SubmitHandler<TOutputType>;
  /** Optional callback when client-side validation fails (e.g. show a toast). */
  onInvalid?: SubmitErrorHandler<TFieldValues>;
  /** Boolean indicating if the form is currently in a submitting state (e.g., waiting for API response). */
  isSubmitting: boolean;
  /** Allows explicitly disabling the submit button, independent of the `isSubmitting` state. */
  isSubmitDisabled?: boolean;
  /** Allows explicitly disabling the secondary submit button. */
  isSecondarySubmitDisabled?: boolean;
  /** Optional callback for a 'Cancel' action. Button is omitted if undefined. */
  onCancel?: () => void;
  /** Optional context object that can be passed to the Zod validation schema via the resolver. */
  context?: TContext;
  /** Optional children to render inside the form. */
  children?: React.ReactNode;
  /** Server-returned validation errors to show both by the field and in the accessible summary. */
  serverErrors?: FormServerErrors;
  /** Reports dirty state so a route or tab owner can warn before discarding edits. */
  onDirtyChange?: (isDirty: boolean) => void;
  /** Enables warnings before browser, document-link, or router navigation discards dirty values. */
  guardUnsavedChanges?: boolean;
  /** Uses a data router to block SPA navigation before dirty form state can unmount. */
  blockRouterNavigation?: boolean;
  /** Includes dirty state owned by custom form children outside React Hook Form. */
  hasExternalUnsavedChanges?: boolean;
  ref?: Ref<FormRef<TFieldValues>>;
}

type ResolvedFormProps<
  TFieldValues extends FieldValues,
  TContext = Record<string, unknown>,
  TOutputType extends FieldValues = TFieldValues,
> = Omit<
  FormProps<TFieldValues, TContext, TOutputType>,
  'fieldsGap' | 'guardUnsavedChanges' | 'blockRouterNavigation' | 'hasExternalUnsavedChanges'
> & {
  fieldsGap: SDSFlexboxGap;
  guardUnsavedChanges: boolean;
  blockRouterNavigation: boolean;
  hasExternalUnsavedChanges: boolean;
};

function resolveFormProps<
  TFieldValues extends FieldValues,
  TContext = Record<string, unknown>,
  TOutputType extends FieldValues = TFieldValues,
>(props: FormProps<TFieldValues, TContext, TOutputType>): ResolvedFormProps<TFieldValues, TContext, TOutputType> {
  return {
    ...props,
    fieldsGap: props.fieldsGap ?? 'content-large',
    guardUnsavedChanges: props.guardUnsavedChanges ?? false,
    blockRouterNavigation: props.blockRouterNavigation ?? false,
    hasExternalUnsavedChanges: props.hasExternalUnsavedChanges ?? false,
  };
}

/**
 * A generic and configurable form component powered by React Hook Form and Zod.
 * It dynamically renders fields and groups based on a configuration array,
 * handles validation, submission, and provides an imperative API via its React 19 ref prop.
 */
export function Form<
  TFieldValues extends FieldValues,
  TContext = Record<string, unknown>,
  TOutputType extends FieldValues = TFieldValues,
>(props: FormProps<TFieldValues, TContext, TOutputType>): ReactElement {
  const {
    formConfig,
    fieldsGap,
    submitButtonLabel,
    secondarySubmitButtonLabel,
    validationSchema,
    defaultValues,
    onSubmit,
    onSecondarySubmit,
    onInvalid,
    isSubmitting,
    isSubmitDisabled,
    isSecondarySubmitDisabled,
    submitBtnTestId,
    onCancel,
    context,
    children,
    serverErrors,
    onDirtyChange,
    guardUnsavedChanges,
    blockRouterNavigation,
    hasExternalUnsavedChanges,
    ref,
  } = resolveFormProps(props);
  // Initialize react-hook-form, connecting it with the Zod schema via the resolver.
  const methods = useForm<TFieldValues, TContext, TOutputType>({
    resolver: zodResolver(validationSchema as Parameters<typeof zodResolver>[0]) as Resolver<
      TFieldValues,
      TContext,
      TOutputType
    >,
    defaultValues, // Set initial form values.
    context, // Provide optional context to the resolver (and subsequently to Zod).
  });
  const {
    control,
    clearErrors,
    formState: { errors, isDirty, submitCount },
    handleSubmit,
    getValues,
    register,
    reset,
    setError,
  } = methods;
  const [isSecondaryAction, setIsSecondaryAction] = useState(false);

  const isPrimarySubmitting = isSubmitting && !isSecondaryAction;
  const isSecondarySubmitting = isSubmitting && isSecondaryAction;

  const submitHandler = handleSubmit((data, event) => {
    setIsSecondaryAction(false);
    return onSubmit(data, event);
  }, onInvalid);

  const secondarySubmitHandler = onSecondarySubmit
    ? handleSubmit((data, event) => {
        setIsSecondaryAction(true);
        return onSecondarySubmit(data, event);
      }, onInvalid)
    : undefined;
  const { errorSummaryItems, errorSummaryRef, errorSummaryTitleId } = useFormErrorSummary({
    errors,
    serverErrors,
    submitCount,
  });
  const { hasUnsavedChangesRef } = useFormUnsavedChangesGuard({
    isDirty,
    hasExternalUnsavedChanges,
    onDirtyChange,
    guardUnsavedChanges,
    blockRouterNavigation,
  });
  useFormServerErrorsSync({ serverErrors, clearErrors, setError });

  // Expose the `submitForm` and `register` functions to parent components via the ref.
  // This allows triggering submission programmatically from outside the form.
  useImperativeHandle(ref, () => ({
    submitForm: submitHandler, // Ensure validation runs even on imperative submit.
    register: register as UseFormRegister<FieldValues>,
    control: control as Control<FieldValues>,
    setFieldError: (name, message) => setError(name, { type: 'server', message }),
    hasUnsavedChanges: () => hasUnsavedChangesRef.current,
    markClean: () => {
      hasUnsavedChangesRef.current = false;
      reset(getValues());
    },
  }));

  // Determine button layout based on whether the Cancel button is present.
  const isFullWidth = !onCancel;

  return (
    // FormProvider exposes the same `useForm` instance to children via
    // `useFormContext`, so custom-rendered children (passed via `children` or
    // FormCustomField) can call `useWatch` / `setValue` without prop-drilling
    // the control object.
    <FormProvider {...methods}>
      {guardUnsavedChanges && blockRouterNavigation && (
        <Suspense fallback={null}>
          <RouterUnsavedChangesPrompt isDirty={() => hasUnsavedChangesRef.current} />
        </Suspense>
      )}
      {/* The native form element uses RHF's handleSubmit to manage the submit event. */}
      <form onSubmit={submitHandler} noValidate style={{ width: '100%' }}>
        <SDSFlexbox gap="content-default" direction="column">
          <FormErrorSummary items={errorSummaryItems} containerRef={errorSummaryRef} titleId={errorSummaryTitleId} />
          {/* Main flex container for arranging form fields and groups */}
          <SDSFlexbox gap={fieldsGap} wrap="wrap">
            {formConfig.map((formConfigItem, index) => (
              <FormConfigItemRenderer
                key={getFormConfigItemKey(formConfigItem, index)}
                item={formConfigItem}
                control={control}
                isDisabled={isSubmitting}
                gap={fieldsGap}
              />
            ))}
          </SDSFlexbox>

          {children}

          {/* Render the action buttons only if at least one is configured. */}
          <FormActionButtons
            onCancel={onCancel}
            submitButtonLabel={submitButtonLabel}
            secondarySubmitButtonLabel={secondarySubmitButtonLabel}
            secondarySubmitHandler={secondarySubmitHandler}
            isSubmitting={isSubmitting}
            isPrimarySubmitting={isPrimarySubmitting}
            isSubmitDisabled={isSubmitDisabled}
            isSecondarySubmitting={isSecondarySubmitting}
            isSecondarySubmitDisabled={isSecondarySubmitDisabled}
            submitBtnTestId={submitBtnTestId}
            isFullWidth={isFullWidth}
          />
        </SDSFlexbox>
      </form>
    </FormProvider>
  );
}
